import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { 
  Search, RefreshCw, Loader2, ArrowUpRight, ArrowDownLeft,
  ChevronLeft, ChevronRight, X, FileText, Eye, 
  Users, Link2, Plus, ZoomIn, ZoomOut, ExternalLink,
  Calendar, Filter, AlertTriangle, CheckCircle
} from "lucide-react";
import axios from "axios";
import { TransactionActionMenu } from "./components/TransactionActionMenu";
import { TransactionDetailSheet } from "./components/TransactionDetailSheet";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUT_LABELS = {
  non_rapproche: { label: "Non justifiée", color: "bg-orange-100 text-orange-700 border-orange-200" },
  en_attente_validation: { label: "À valider", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  rapproche: { label: "Justifiée", color: "bg-green-100 text-green-700 border-green-200" },
  rapproche_auto: { label: "Justifiée", color: "bg-green-100 text-green-700 border-green-200" },
  rapproche_manuel: { label: "Justifiée", color: "bg-green-100 text-green-700 border-green-200" },
  rapproche_groupe: { label: "Paiement groupé", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  rapproche_remise_sepa: { label: "Remise SEPA", color: "bg-purple-100 text-purple-700 border-purple-200" },
  divers: { label: "Traité", color: "bg-slate-100 text-slate-700 border-slate-200" },
  ignore: { label: "Ignoré", color: "bg-red-100 text-red-700 border-red-200" },
  sans_correspondance: { label: "Sans correspondance", color: "bg-amber-100 text-amber-700 border-amber-200" },
};

export default function Transactions() {
  const { token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ solde: 0, nonJustifiees: 0, soldeBancaire: 0 });
  const pageSize = 25;
  
  // Filtre par compte
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  
  // Filtre par date
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Détail transaction (panneau latéral)
  const [selectedTx, setSelectedTx] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [remiseDetail, setRemiseDetail] = useState(null);
  const [factureDetail, setFactureDetail] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  // Actions
  const [unlinking, setUnlinking] = useState(false);
  const [matching, setMatching] = useState(false);

  // Modal rapprochement manuel
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [searchFacture, setSearchFacture] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchingFactures, setSearchingFactures] = useState(false);
  const [selectedFactures, setSelectedFactures] = useState([]);
  const [facturePage, setFacturePage] = useState(1);
  const [totalFactures, setTotalFactures] = useState(0);

  // PDF Preview
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfZoom, setPdfZoom] = useState(100);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Charger la liste des comptes
  useEffect(() => {
    const fetchAccounts = async () => {
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const res = await axios.get(`${API}/api/bridge-v2/accounts`, { headers });
        const activeAccounts = (res.data.accounts || []).filter(a => a.status !== 'disconnected');
        setAccounts(activeAccounts);
      } catch (err) {
        console.error("Erreur chargement comptes:", err);
      }
    };
    fetchAccounts();
  }, [token]);

  // Mapper activeTab vers le filtre statut API
  const getStatutFromTab = useCallback((tab) => {
    switch (tab) {
      case "non_justifiee": return "non_rapproche,sans_correspondance,en_attente_validation";
      case "justifiee": return "rapproche,rapproche_auto,rapproche_manuel,rapproche_groupe,rapproche_remise_sepa";
      case "traite": return "divers,ignore";
      default: return null;
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setLoading(true);
      const params = new URLSearchParams({
        skip: ((page - 1) * pageSize).toString(),
        limit: pageSize.toString(),
      });
      
      if (debouncedSearch) params.append("search", debouncedSearch);
      const statutFilter = getStatutFromTab(activeTab);
      if (statutFilter) params.append("statut", statutFilter);
      
      // Filtre par compte
      if (selectedAccountId) params.append("account_id", selectedAccountId);
      
      // Filtre par date
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);

      const res = await axios.get(`${API}/api/bridge-v2/transactions?${params}`, { headers });
      setTransactions(res.data.transactions || []);
      setTotalCount(res.data.total || 0);
      setTotalPages(Math.ceil((res.data.total || 0) / pageSize));
      
      // Mettre à jour les stats
      if (res.data.stats) {
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error("Erreur chargement transactions:", err);
    } finally {
      setLoading(false);
    }
  }, [token, page, debouncedSearch, activeTab, selectedAccountId, dateFrom, dateTo, getStatutFromTab]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Charger les stats globales au premier chargement
  useEffect(() => {
    const fetchStats = async () => {
      if (!token) return;
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/api/bridge-v2/transactions/stats`, { headers });
        if (res.data) {
          setStats(res.data);
        }
      } catch (err) {
        console.log("Stats non disponibles");
      }
    };
    fetchStats();
  }, [token]);

  // Charger les détails quand une transaction est sélectionnée
  useEffect(() => {
    if (selectedTx) {
      loadTransactionDetail(selectedTx);
    } else {
      setRemiseDetail(null);
      setFactureDetail(null);
    }
  }, [selectedTx?.id]);

  const loadTransactionDetail = async (tx) => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    setLoadingDetail(true);
    setRemiseDetail(null);
    setFactureDetail(null);
    setSuggestions([]);

    try {
      // Détecter si c'est une transaction REM SDD
      const desc = (tx.clean_description || tx.provider_description || '').toLowerCase();
      const isRemSddTx = desc.includes('rem sdd') || desc.includes('rem. sdd') || 
                         tx.remise_sepa_id || tx.statut_rapprochement === 'rapproche_remise_sepa';
      
      if (isRemSddTx) {
        // Essayer de charger la remise SEPA associée
        if (tx.remise_sepa_id) {
          const res = await axios.get(`${API}/api/bridge-v2/remises/${tx.remise_sepa_id}`, { headers });
          setRemiseDetail(res.data);
        } else {
          // Chercher par transaction ID ou par montant/date
          try {
            const res = await axios.get(`${API}/api/bridge-v2/remises/by-transaction/${tx.id}`, { headers });
            if (res.data) setRemiseDetail(res.data);
          } catch (e) {
            console.log("Remise not found for transaction - peut-être pas encore importée");
          }
        }
      }
      
      if (tx.facture_rapprochee_numero) {
        const isVente = tx.facture_rapprochee_type === 'vente';
        const endpoint = isVente 
          ? `${API}/api/invoices/${tx.facture_rapprochee_numero}`
          : `${API}/api/supplier-invoices/${tx.facture_rapprochee_numero}`;
        
        try {
          const res = await axios.get(endpoint, { headers });
          setFactureDetail(res.data);
        } catch (e) {
          console.log("Facture detail not found");
        }
      }
      
      // Charger les suggestions si la transaction a des suggestions stockées
      if (tx.suggestions && tx.suggestions.length > 0) {
        setSuggestions(tx.suggestions);
      } else if (!tx.facture_rapprochee_numero && canMatch(tx)) {
        // Sinon, chercher des factures correspondantes
        try {
          const isDebit = tx.amount < 0;
          const montant = Math.abs(tx.amount);
          const baseEndpoint = isDebit ? `${API}/api/supplier-invoices` : `${API}/api/invoices`;
          const params = new URLSearchParams({
            limit: '5',
            skip: '0',
            priorite_montant: montant.toString()
          });
          
          const res = await axios.get(`${baseEndpoint}?${params}`, { headers });
          const invoices = res.data.invoices || res.data || [];
          
          // Filtrer et scorer les factures
          const scored = invoices.map(inv => {
            const isSameMontant = Math.abs((inv.total_ttc || 0) - montant) < 0.01;
            const clientName = (inv.client?.nom || inv.client || inv.fournisseur || '').toLowerCase();
            const txDesc = (tx.clean_description || tx.provider_description || '').toLowerCase();
            const isNameMatch = clientName && txDesc && (clientName.includes(txDesc.substring(0, 5)) || txDesc.includes(clientName.substring(0, 5)));
            
            return {
              ...inv,
              score: (isSameMontant ? 50 : 0) + (isNameMatch ? 30 : 0),
              isSameMontant,
              isNameMatch,
              isProbable: isSameMontant && isNameMatch
            };
          }).filter(inv => inv.score > 0).sort((a, b) => b.score - a.score);
          
          setSuggestions(scored.slice(0, 3));
        } catch (e) {
          console.log("Erreur chargement suggestions:", e);
        }
      }
    } catch (err) {
      console.error("Erreur chargement détail:", err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSync = async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setSyncing(true);
      await axios.post(`${API}/api/bridge-v2/sync`, {}, { headers });
      await fetchTransactions();
    } catch (err) {
      console.error("Erreur sync:", err);
    } finally {
      setSyncing(false);
    }
  };

  const handleAutoReconciliation = async () => {
    if (!token || autoMatching) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setAutoMatching(true);
      const res = await axios.post(`${API}/api/bridge-v2/rapprochement/auto`, {}, { headers });
      const result = res.data;
      
      // Rafraîchir les données
      await fetchTransactions();
      
      // Rafraîchir les stats
      const statsRes = await axios.get(`${API}/api/bridge-v2/transactions/stats`, { headers });
      if (statsRes.data) setStats(statsRes.data);
      
      // Afficher le résultat
      const message = `Rapprochement terminé !\n\n` +
        `• Transactions analysées : ${result.total_traitees}\n` +
        `• Rapprochements auto : ${result.auto_rapprochees}\n` +
        `• Sans correspondance : ${result.sans_correspondance}\n` +
        `• Divers/Frais : ${result.divers}`;
      alert(message);
    } catch (err) {
      console.error("Erreur rapprochement auto:", err);
      alert(err.response?.data?.detail || "Erreur lors du rapprochement automatique");
    } finally {
      setAutoMatching(false);
    }
  };

  const handleUnlink = async () => {
    if (!token || !selectedTx || unlinking) return;
    
    if (!window.confirm(`Êtes-vous sûr de vouloir annuler le rapprochement de cette transaction ?`)) {
      return;
    }
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setUnlinking(true);
      await axios.post(`${API}/api/bridge-v2/rapprochement/unlink/${selectedTx.id}`, {}, { headers });
      await fetchTransactions();
      setSelectedTx(null);
      setRemiseDetail(null);
      setFactureDetail(null);
    } catch (err) {
      console.error("Erreur unlink:", err);
      alert(err.response?.data?.detail || "Erreur lors de la déliaison");
    } finally {
      setUnlinking(false);
    }
  };

  const handleOpenMatchModal = (tx = null) => {
    if (tx) setSelectedTx(tx);
    setSearchFacture("");
    setSearchResults([]);
    setSelectedFactures([]);
    setFacturePage(1);
    setShowMatchModal(true);
    loadFactures("", 1, tx || selectedTx);
  };

  const loadFactures = async (query = "", page = 1, tx = selectedTx) => {
    if (!token || !tx) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setSearchingFactures(true);
      const isDebit = tx.amount < 0;
      const montant = Math.abs(tx.amount);
      const txDate = tx.date;
      const limit = 20;
      const skip = (page - 1) * limit;
      
      const baseEndpoint = isDebit ? `${API}/api/supplier-invoices` : `${API}/api/invoices`;
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString(),
        priorite_montant: montant.toString()
      });
      
      if (query) params.append("search", query);
      
      const res = await axios.get(`${baseEndpoint}?${params}`, { headers });
      
      let invoices = res.data.invoices || res.data || [];
      const total = res.data.total || invoices.length;
      
      const txMonth = txDate ? txDate.substring(0, 7) : null;
      invoices = invoices.map(inv => {
        const isSameMontant = Math.abs((inv.total_ttc || 0) - montant) < 0.01;
        const invMonth = inv.date ? inv.date.substring(0, 7) : null;
        const isSameMonth = txMonth && invMonth && txMonth === invMonth;
        return {
          ...inv,
          _isProbable: isSameMontant && isSameMonth,
          _isSameMontant: isSameMontant
        };
      });
      
      setSearchResults(invoices);
      setTotalFactures(total);
    } catch (err) {
      console.error("Erreur chargement factures:", err);
      setSearchResults([]);
    } finally {
      setSearchingFactures(false);
    }
  };

  const handleSearchFactures = async () => {
    setFacturePage(1);
    loadFactures(searchFacture, 1);
  };

  const toggleFactureSelection = (facture) => {
    const isSelected = selectedFactures.some(f => f.numero === facture.numero);
    if (isSelected) {
      setSelectedFactures(prev => prev.filter(f => f.numero !== facture.numero));
    } else {
      setSelectedFactures(prev => [...prev, facture]);
    }
  };

  const selectedTotal = selectedFactures.reduce((sum, f) => sum + (f.total_ttc || 0), 0);
  const txAmount = Math.abs(selectedTx?.amount || 0);
  const difference = Math.abs(selectedTotal - txAmount);
  const isExactMatch = difference < 0.01;

  const handleValidateMatch = async () => {
    if (!token || !selectedTx || matching || selectedFactures.length === 0) return;
    if (!isExactMatch) {
      alert(`Le montant ne correspond pas exactement.\nTransaction: ${formatCurrency(txAmount)}\nFactures: ${formatCurrency(selectedTotal)}\nDifférence: ${formatCurrency(difference)}`);
      return;
    }
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setMatching(true);
      const isVente = selectedTx.amount > 0;
      
      if (selectedFactures.length === 1) {
        await axios.post(`${API}/api/bridge-v2/rapprochement/validate`, {
          transaction_id: selectedTx.id,
          facture_numero: selectedFactures[0].numero,
          facture_type: isVente ? 'vente' : 'achat',
          mode: 'manuel'
        }, { headers });
      } else {
        await axios.post(`${API}/api/bridge-v2/rapprochement/validate-groupe`, {
          transaction_id: selectedTx.id,
          factures: selectedFactures.map(f => ({
            numero: f.numero,
            montant: f.total_ttc,
            client: f.client?.nom || f.client || f.fournisseur || '-'
          })),
          facture_type: isVente ? 'vente' : 'achat'
        }, { headers });
      }
      
      setShowMatchModal(false);
      setSelectedFactures([]);
      await fetchTransactions();
      setSelectedTx(null);
    } catch (err) {
      console.error("Erreur rapprochement:", err);
      alert(err.response?.data?.detail || "Erreur lors du rapprochement");
    } finally {
      setMatching(false);
    }
  };

  // Valider une suggestion en 1 clic
  const handleValidateSuggestion = async (facture) => {
    if (!token || !selectedTx || matching) return;
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setMatching(true);
      const isVente = selectedTx.amount > 0;
      
      await axios.post(`${API}/api/bridge-v2/rapprochement/validate`, {
        transaction_id: selectedTx.id,
        facture_numero: facture.numero,
        facture_type: isVente ? 'vente' : 'achat',
        mode: 'suggestion'
      }, { headers });
      
      await fetchTransactions();
      setSelectedTx(null);
      setSuggestions([]);
    } catch (err) {
      console.error("Erreur validation suggestion:", err);
      alert(err.response?.data?.detail || "Erreur lors du rapprochement");
    } finally {
      setMatching(false);
    }
  };

  const handleClasserDivers = async (tx = selectedTx) => {
    if (!token || !tx) return;
    
    const libelle = prompt("Libellé pour cette opération diverse :", tx.clean_description || "");
    if (!libelle) return;
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      await axios.post(`${API}/api/bridge-v2/rapprochement/divers`, {
        transaction_id: tx.id,
        libelle: libelle
      }, { headers });
      
      await fetchTransactions();
      if (selectedTx?.id === tx.id) setSelectedTx(null);
    } catch (err) {
      console.error("Erreur classement divers:", err);
      alert(err.response?.data?.detail || "Erreur");
    }
  };

  const handleIgnorer = async (tx = selectedTx) => {
    if (!token || !tx) return;
    
    if (!window.confirm("Ignorer cette transaction ?")) return;
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      await axios.post(`${API}/api/bridge-v2/rapprochement/ignore`, {
        transaction_id: tx.id
      }, { headers });
      
      await fetchTransactions();
      if (selectedTx?.id === tx.id) setSelectedTx(null);
    } catch (err) {
      console.error("Erreur ignorer:", err);
      alert(err.response?.data?.detail || "Erreur");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'Europe/Paris'
    });
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone: 'Europe/Paris'
    });
  };

  const handleViewPdf = (factureNumero, type = 'vente') => {
    if (!token || !factureNumero) return;
    
    const endpoint = type === 'vente' 
      ? `${API}/api/pdfs/${factureNumero}?token=${token}`
      : `${API}/api/supplier-invoices/${factureNumero}/pdf?token=${token}`;
    
    setPdfUrl(endpoint);
    setShowPdfModal(true);
  };

  const isRemSdd = (tx) => {
    const desc = (tx.clean_description || tx.provider_description || '').toLowerCase();
    return desc.includes('rem sdd') || desc.includes('rem. sdd') || tx.statut_rapprochement === 'rapproche_remise_sepa';
  };

  const isRapprochee = (tx) => {
    // Pour les remises SEPA, vérifier si tous les prélèvements sont rapprochés
    if (tx.statut_rapprochement === 'rapproche_remise_sepa') {
      return tx.remise_complete === true;
    }
    return ['rapproche', 'rapproche_auto', 'rapproche_manuel', 'rapproche_groupe'].includes(tx.statut_rapprochement);
  };

  const canMatch = (tx) => {
    return !isRapprochee(tx) && tx.statut_rapprochement !== 'divers' && tx.statut_rapprochement !== 'ignore';
  };

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col" data-testid="transactions-page">
      {/* En-tête compact avec solde Bridge et stats inline */}
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-bold text-slate-900">Transactions</h1>
          <div className="flex items-center gap-4 text-sm">
            {/* Soldes des comptes (fournis par Bridge) */}
            {accounts.map((acc) => (
              <div key={acc.id} className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-md border border-slate-200" title={`Dernière sync: ${acc.last_sync_at ? new Date(acc.last_sync_at).toLocaleString('fr-FR') : 'N/A'}`}>
                <span className="text-slate-500 text-xs">{acc.name?.replace('Compte ', '').substring(0, 12)}:</span>
                <span className={`font-semibold ${(acc.balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(acc.balance || 0)}
                </span>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <span className="text-slate-500">Non justifiées:</span>
              <span className="font-semibold text-orange-600">{formatCurrency(stats.montantNonJustifiees || 0)}</span>
              {stats.nonJustifiees > 0 && (
                <Badge className="bg-red-500 text-white text-[10px] h-4 min-w-4 px-1 rounded-full">
                  {stats.nonJustifiees}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleAutoReconciliation} 
            disabled={autoMatching}
            size="sm"
            variant="outline"
            className="h-8 border-blue-200 text-blue-700 hover:bg-blue-50"
            data-testid="auto-match-btn"
          >
            {autoMatching ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Link2 className="w-3 h-3 mr-1" />
            )}
            Rapprocher auto
          </Button>
          <Button 
            onClick={handleSync} 
            disabled={syncing}
            size="sm"
            className="bg-green-600 hover:bg-green-700 h-8"
            data-testid="sync-btn"
          >
            {syncing ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <CheckCircle className="w-3 h-3 mr-1" />
            )}
            Synchronisé
          </Button>
        </div>
      </div>

      {/* Barre de filtres compacte */}
      <div className="flex items-center gap-3 mb-2 px-1">
        <div className="relative flex-1 max-w-[250px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm border-slate-200"
            data-testid="search-input"
          />
        </div>
        
        {/* Filtre par date - responsive */}
        <div className="flex items-center gap-1">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="h-8 text-xs border-slate-200 w-24 sm:w-32 lg:w-36"
            placeholder="Du"
          />
          <span className="text-slate-400 text-xs hidden sm:inline">→</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="h-8 text-xs border-slate-200 w-24 sm:w-32 lg:w-36"
            placeholder="Au"
          />
          {(dateFrom || dateTo) && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 px-2"
              onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
            >
              <X className="w-3 h-3" />
            </Button>
          )}
        </div>

        {/* Filtre par compte */}
        {accounts.length > 1 && (
          <select
            value={selectedAccountId}
            onChange={(e) => { setSelectedAccountId(e.target.value); setPage(1); }}
            className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white"
          >
            <option value="">Tous les comptes</option>
            {accounts.map(acc => (
              <option key={acc.bridge_account_id} value={acc.bridge_account_id}>
                {acc.name} ({acc.provider_name})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Onglets compacts */}
      <div className="flex items-center justify-between mb-1 px-1">
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(1); }}>
          <TabsList className="bg-transparent p-0 h-auto gap-3">
            <TabsTrigger 
              value="all" 
              className="text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 rounded-none pb-1 px-1"
            >
              Tout <span className="ml-1 text-slate-400 text-xs">{stats.total || totalCount}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="non_justifiee"
              className="text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-orange-500 data-[state=active]:text-orange-600 rounded-none pb-1 px-1"
            >
              Non justifiée <span className="ml-1 text-slate-400 text-xs">{stats.nonJustifiees || 0}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="justifiee"
              className="text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-green-500 data-[state=active]:text-green-600 rounded-none pb-1 px-1"
            >
              Justifiée <span className="ml-1 text-slate-400 text-xs">{stats.justifiees || 0}</span>
            </TabsTrigger>
            <TabsTrigger 
              value="traite"
              className="text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-slate-500 data-[state=active]:text-slate-600 rounded-none pb-1 px-1"
            >
              Traité <span className="ml-1 text-slate-400 text-xs">{stats.traites || 0}</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Split View: Table + Détails */}
      <div className="flex-1 flex min-h-0 border rounded-lg overflow-hidden bg-white">
        {/* Table des transactions */}
        <div className={`${selectedTx ? 'w-3/5 border-r' : 'w-full'} flex flex-col min-h-0`}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <p>Aucune transaction trouvée</p>
            </div>
          ) : (
            <div className="overflow-auto flex-1">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b sticky top-0 z-10">
                  <tr>
                    <th className="w-8 px-2 py-2">
                      <Checkbox className="h-3.5 w-3.5" />
                    </th>
                    <th className="w-6 px-1 py-2"></th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-slate-500 uppercase">Statut</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-slate-500 uppercase">Date</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-slate-500 uppercase">Libellé</th>
                    {accounts.length > 1 && (
                      <th className="text-left px-2 py-2 text-[10px] font-medium text-slate-500 uppercase">Compte</th>
                    )}
                    <th className="text-right px-2 py-2 text-[10px] font-medium text-slate-500 uppercase">Montant</th>
                    <th className="text-center px-2 py-2 text-[10px] font-medium text-slate-500 uppercase">Justif.</th>
                    <th className="w-8 px-1 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {transactions.map((tx) => {
                    const isCredit = tx.amount > 0;
                    // Pour les remises SEPA incomplètes, afficher "Remise incomplète" en orange
                    let statut;
                    if (tx.statut_rapprochement === 'rapproche_remise_sepa' && tx.remise_complete === false) {
                      statut = { 
                        label: `Remise SEPA (${tx.remise_prelevements_lies || 0}/${tx.remise_prelevements_total || '?'})`, 
                        color: "bg-orange-100 text-orange-700 border-orange-200" 
                      };
                    } else {
                      statut = STATUT_LABELS[tx.statut_rapprochement] || STATUT_LABELS.non_rapproche;
                    }
                    const isSelected = selectedTx?.id === tx.id;
                    const isSdd = isRemSdd(tx);
                    const txRapprochee = isRapprochee(tx);
                    const txCanMatch = canMatch(tx);
                    
                    return (
                      <tr 
                        key={tx.id || tx.bridge_transaction_id} 
                        onClick={() => setSelectedTx(isSelected ? null : tx)}
                        className={`cursor-pointer transition-colors ${
                          isSelected 
                            ? 'bg-blue-50' 
                            : 'hover:bg-slate-50'
                        }`}
                        data-testid={`tx-row-${tx.bridge_transaction_id}`}
                      >
                        <td className="px-2 py-1.5">
                          <Checkbox className="h-3.5 w-3.5" onClick={(e) => e.stopPropagation()} />
                        </td>
                        <td className="px-1 py-1.5">
                          {txRapprochee ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge className={`${statut.color} border font-normal text-[10px] px-1.5 py-0`}>
                            {statut.label}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 text-xs text-slate-600 whitespace-nowrap">
                          {formatDate(tx.date)}
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-slate-900 truncate max-w-[180px]">
                              {tx.clean_description || tx.provider_description || '-'}
                            </span>
                            {isSdd && (
                              <Badge className="bg-purple-100 text-purple-700 text-[9px] px-1 py-0">
                                SEPA
                              </Badge>
                            )}
                          </div>
                        </td>
                        {accounts.length > 1 && (
                          <td className="px-2 py-1.5">
                            <span className="text-[10px] text-slate-500 truncate max-w-[100px] block">
                              {tx.account_name || 'Compte'}
                            </span>
                          </td>
                        )}
                        <td className={`px-2 py-1.5 text-right text-xs font-semibold whitespace-nowrap ${
                          isCredit ? 'text-green-600' : 'text-slate-900'
                        }`}>
                          {isCredit ? '+' : ''}{formatCurrency(tx.amount)}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {/* Paiement groupé - plusieurs factures */}
                          {tx.factures_liees && tx.factures_liees.length > 1 ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTx(tx);
                              }}
                              className="relative w-6 h-6 rounded bg-indigo-100 flex items-center justify-center hover:bg-indigo-200 transition-colors mx-auto"
                              title={`${tx.factures_liees.length} factures liées`}
                            >
                              <FileText className="w-3 h-3 text-indigo-600" />
                              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-indigo-600 text-white text-[8px] rounded-full flex items-center justify-center font-bold">
                                {tx.factures_liees.length}
                              </span>
                            </button>
                          ) : tx.facture_rapprochee_numero ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewPdf(tx.facture_rapprochee_numero, tx.facture_rapprochee_type);
                              }}
                              className="w-6 h-6 rounded bg-green-100 flex items-center justify-center hover:bg-green-200 transition-colors mx-auto"
                              title={tx.facture_rapprochee_numero}
                            >
                              <FileText className="w-3 h-3 text-green-600" />
                            </button>
                          ) : (tx.remise_sepa_id || isSdd) ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTx(tx);
                              }}
                              className="relative w-6 h-6 rounded bg-purple-100 flex items-center justify-center hover:bg-purple-200 transition-colors mx-auto"
                              title="Voir le détail de la remise SEPA"
                            >
                              <Users className="w-3 h-3 text-purple-600" />
                              {tx.remise_sepa_id && (
                                <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-purple-600 text-white text-[8px] rounded-full flex items-center justify-center">
                                  ✓
                                </span>
                              )}
                            </button>
                          ) : txCanMatch ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenMatchModal(tx);
                              }}
                              className="w-6 h-6 rounded border border-dashed border-slate-300 flex items-center justify-center hover:border-blue-400 hover:bg-blue-50 transition-colors mx-auto"
                            >
                              <Plus className="w-3 h-3 text-slate-400" />
                            </button>
                          ) : (
                            <span className="text-slate-300 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-1 py-1.5" onClick={(e) => e.stopPropagation()}>
                          <TransactionActionMenu
                            transaction={tx}
                            onRapprocher={() => handleOpenMatchModal(tx)}
                            onRapprochementPartiel={() => handleOpenMatchModal(tx)}
                            onRapprochementAvance={() => handleOpenMatchModal(tx)}
                            onClasserTraite={() => handleClasserDivers(tx)}
                            onIgnorer={() => handleIgnorer(tx)}
                            onDelier={handleUnlink}
                            isRapprochee={txRapprochee}
                            canMatch={txCanMatch}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination compacte */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t bg-slate-50">
              <p className="text-xs text-slate-500">
                Page {page}/{totalPages} • {totalCount} transactions
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-3 h-3" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Panneau de détails compact */}
        {selectedTx && (
          <div className="w-2/5 min-h-0">
            <TransactionDetailSheet
              transaction={selectedTx}
              remiseDetail={remiseDetail}
              factureDetail={factureDetail}
              loadingDetail={loadingDetail}
              suggestions={suggestions}
              onClose={() => setSelectedTx(null)}
              onOpenMatchModal={() => handleOpenMatchModal()}
              onViewPdf={handleViewPdf}
              onUnlink={handleUnlink}
              onValidateSuggestion={handleValidateSuggestion}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              formatDateLong={formatDateLong}
            />
          </div>
        )}
      </div>

      {/* Modal Rapprochement Manuel */}
      <Dialog open={showMatchModal} onOpenChange={(open) => { setShowMatchModal(open); if (!open) setSelectedFactures([]); }}>
        <DialogContent className="max-w-3xl h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
            <DialogTitle>
              Rapprocher avec {selectedTx?.amount > 0 ? "des factures ventes" : "des factures achats"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col px-6 min-h-0">
            {/* Transaction à rapprocher */}
            <div className="p-3 bg-slate-50 rounded-lg flex items-center justify-between my-4 flex-shrink-0">
              <div>
                <p className="text-sm text-slate-600">Transaction à rapprocher :</p>
                <p className="font-medium">{selectedTx?.clean_description}</p>
                <p className="text-xs text-slate-500">{formatDate(selectedTx?.date)}</p>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold ${selectedTx?.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(Math.abs(selectedTx?.amount || 0))}
                </p>
              </div>
            </div>

            {/* Barre de recherche */}
            <div className="flex gap-2 mb-4 flex-shrink-0">
              <Input
                placeholder="Rechercher par numéro, client, fournisseur..."
                value={searchFacture}
                onChange={(e) => setSearchFacture(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchFactures()}
              />
              <Button onClick={handleSearchFactures} disabled={searchingFactures}>
                {searchingFactures ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {/* Liste des factures - zone scrollable */}
            <div className="flex-1 overflow-y-auto border rounded-lg min-h-0">
              {searchingFactures ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <p>Aucune facture trouvée</p>
                  <p className="text-sm">Utilisez la recherche ou vérifiez les filtres</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="w-10 px-3 py-2"></th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Numéro</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">
                        {selectedTx?.amount > 0 ? 'Client' : 'Fournisseur'}
                      </th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Date</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-slate-500">Montant TTC</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {searchResults.map((fac) => {
                      const isSelected = selectedFactures.some(f => f.numero === fac.numero);
                      const clientName = fac.client?.nom || fac.client || fac.fournisseur || '-';
                      const isProbable = fac._isProbable;
                      const isSameMontant = fac._isSameMontant;
                      
                      return (
                        <tr 
                          key={fac.numero}
                          onClick={() => toggleFactureSelection(fac)}
                          className={`cursor-pointer transition-colors ${
                            isSelected 
                              ? 'bg-blue-50' 
                              : isProbable 
                                ? 'bg-green-50 border-l-4 border-l-green-500' 
                                : isSameMontant 
                                  ? 'bg-yellow-50' 
                                  : 'hover:bg-slate-50'
                          }`}
                        >
                          <td className="px-3 py-2 text-center">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => {}}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-blue-600">{fac.numero}</span>
                              {isProbable && (
                                <Badge className="bg-green-100 text-green-700 text-[10px]">
                                  ★ Probable
                                </Badge>
                              )}
                              {!isProbable && isSameMontant && (
                                <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">
                                  Même montant
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-sm text-slate-600 truncate max-w-[200px]">
                            {clientName}
                          </td>
                          <td className="px-3 py-2 text-sm text-slate-500">
                            {formatDate(fac.date)}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">
                            {formatCurrency(fac.total_ttc)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination factures */}
            {totalFactures > 20 && (
              <div className="flex items-center justify-between text-sm py-2 flex-shrink-0">
                <span className="text-slate-500">{totalFactures} factures au total</span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={facturePage === 1}
                    onClick={() => { setFacturePage(p => p - 1); loadFactures(searchFacture, facturePage - 1); }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="px-2 py-1">Page {facturePage}</span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={facturePage * 20 >= totalFactures}
                    onClick={() => { setFacturePage(p => p + 1); loadFactures(searchFacture, facturePage + 1); }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Footer fixe avec sélection et bouton de validation */}
          <div className="border-t bg-white px-6 py-4 flex-shrink-0">
            {/* Factures sélectionnées */}
            {selectedFactures.length > 0 && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-blue-700">
                    {selectedFactures.length} facture{selectedFactures.length > 1 ? 's' : ''} sélectionnée{selectedFactures.length > 1 ? 's' : ''}
                  </p>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedFactures([])}>
                    <X className="w-4 h-4 mr-1" />
                    Tout désélectionner
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedFactures.slice(0, 5).map(f => (
                    <Badge 
                      key={f.numero} 
                      className="bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200"
                      onClick={() => toggleFactureSelection(f)}
                    >
                      {f.numero} • {formatCurrency(f.total_ttc)}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                  {selectedFactures.length > 5 && (
                    <Badge className="bg-slate-100 text-slate-600">
                      +{selectedFactures.length - 5} autres
                    </Badge>
                  )}
                </div>
                
                {/* Comparaison des montants */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-slate-500">Transaction</p>
                    <p className="font-bold">{formatCurrency(txAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Factures</p>
                    <p className="font-bold">{formatCurrency(selectedTotal)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Différence</p>
                    <p className={`font-bold ${isExactMatch ? 'text-green-600' : 'text-red-600'}`}>
                      {isExactMatch ? '✓ OK' : formatCurrency(difference)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Boutons */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowMatchModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleValidateMatch}
                disabled={matching || selectedFactures.length === 0 || !isExactMatch}
                className="min-w-[200px]"
                data-testid="validate-match-btn"
              >
                {matching ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {selectedFactures.length === 0 
                  ? 'Sélectionnez une facture'
                  : selectedFactures.length > 1 
                    ? `Valider (${selectedFactures.length} factures)` 
                    : 'Valider le rapprochement'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal PDF Preview */}
      <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Prévisualisation PDF</DialogTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPdfZoom(z => Math.max(50, z - 25))}>
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-500">{pdfZoom}%</span>
                <Button variant="ghost" size="sm" onClick={() => setPdfZoom(z => Math.min(200, z + 25))}>
                  <ZoomIn className="w-4 h-4" />
                </Button>
                {pdfUrl && (
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" size="sm">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                className="w-full h-full border-0 rounded"
                style={{ transform: `scale(${pdfZoom / 100})`, transformOrigin: 'top left', width: `${10000 / pdfZoom}%`, height: `${10000 / pdfZoom}%` }}
                title="PDF Preview"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
